import React, { useState, useEffect, useRef } from "react"
import DatePicker, { registerLocale } from "react-datepicker"
import { useTheme } from "@mui/material/styles"
import { Box, Slider, Typography, Button } from "@mui/material"
import "react-datepicker/dist/react-datepicker.css"
import { enUS } from "date-fns/locale/en-US"
import { ru } from "date-fns/locale/ru"
import { useLocale } from "ra-core"

//TODO: объединить с i18n компонентом
registerLocale("en", enUS)
registerLocale("ru", ru)

interface DateRangeSliderProps {
  initialStartDate?: Date
  initialEndDate?: Date
  initialCurrentDate?: Date
  onChange: (date: Date) => void
}

export default function DateRangeSlider({
  initialStartDate = new Date(new Date().setDate(new Date().getDate() - 7)),
  initialEndDate = new Date(),
  initialCurrentDate,
  onChange
}: DateRangeSliderProps) {
  const theme = useTheme()
  const locale = useLocale()
  const [startDate, setStartDate] = useState<Date>(initialStartDate)
  const [endDate, setEndDate] = useState<Date>(initialEndDate)
  const [currentDate, setCurrentDate] = useState<Date>(
    initialCurrentDate || new Date(startDate.getTime() + (endDate.getTime() - startDate.getTime()) / 2)
  )
  const [showStartDatePicker, setShowStartDatePicker] = useState<boolean>(false)
  const [showEndDatePicker, setShowEndDatePicker] = useState<boolean>(false)
  const [sliderValue, setSliderValue] = useState<number>(50)
  
  const startDateRef = useRef<HTMLDivElement>(null)
  const endDateRef = useRef<HTMLDivElement>(null)

  // Стили для темной темы
  const darkStyles = {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    '& .react-datepicker__header': {
      backgroundColor: theme.palette.background.paper,
    },
    '& .react-datepicker__day': {
      color: theme.palette.text.primary,
    },
    '& .react-datepicker__day--selected': {
      backgroundColor: theme.palette.primary.main,
    },
    '& .react-datepicker__current-month': {
      color: theme.palette.text.primary,
    },
    '& .react-datepicker__day-name': {
      color: theme.palette.text.secondary,
    }
  }

  useEffect(() => {
    if(initialStartDate) {
      setStartDate(initialStartDate)
    }
   
    if(initialEndDate) {
      setEndDate(initialEndDate)
    }

    if(initialCurrentDate) {
      setCurrentDate(initialCurrentDate)
    }
  }, [initialStartDate, initialEndDate, initialCurrentDate])

  // Calculate slider value based on current date
  useEffect(() => {
    const totalRange = endDate.getTime() - startDate.getTime()
    const currentPosition = currentDate.getTime() - startDate.getTime()
    const percentage = (currentPosition / totalRange) * 100
    setSliderValue(percentage)
  }, [currentDate, startDate, endDate])

  // Update current date when slider value changes
  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const value = newValue as number
    setSliderValue(value)
    
    const totalRange = endDate.getTime() - startDate.getTime()
    const newTimestamp = startDate.getTime() + (totalRange * (value / 100))
    const newDate = new Date(newTimestamp)
    
    setCurrentDate(newDate)
    onChange(newDate)
  }

  // Update start date
  const handleStartDateChange = (date: Date) => {
    if (date < endDate) {
      setStartDate(date)
      setShowStartDatePicker(false)
      
      // Adjust current date if needed
      if (currentDate < date) {
        setCurrentDate(date)
        onChange(date)
      }
    }
  }

  // Update end date
  const handleEndDateChange = (date: Date) => {
    if (date > startDate) {
      setEndDate(date)
      setShowEndDatePicker(false)
      
      // Adjust current date if needed
      if (currentDate > date) {
        setCurrentDate(date)
        onChange(date)
      }
    }
  }

  // Format date for display
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString(locale, { 
      //year: 'numeric', 
      //month: 'short', 
      //day: 'numeric',
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Close date pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        startDateRef.current && 
        !startDateRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('.react-datepicker')
      ) {
        setShowStartDatePicker(false)
      }
      
      if (
        endDateRef.current && 
        !endDateRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('.react-datepicker')
      ) {
        setShowEndDatePicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Box sx={{ width: '100%', padding: 2, }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Start date selector */}
        <Box ref={startDateRef} sx={{ position: 'relative' }}>
          <Button 
            variant="text" 
            size="small"
            onClick={() => setShowStartDatePicker(!showStartDatePicker)}
            sx={{ minWidth: 'auto' }}
          >
            {formatDate(startDate)}
          </Button>
          {showStartDatePicker && (
            <Box sx={{ position: 'absolute', top: '100%', left: 0, zIndex: 10, mt: 1 }}>
              <DatePicker
                selected={startDate}
                onChange={(date) => handleStartDateChange(date!)}
                inline
                maxDate={endDate}
                locale={locale}
                calendarClassName={`material-datepicker ${theme.palette.mode}-theme`}
                popperClassName={`material-datepicker-popper ${theme.palette.mode}-theme`}
                popperPlacement="bottom-start"
                wrapperClassName="date-picker-wrapper"
                calendarContainer={({ className, children }) => (
                  <div style={theme.palette.mode === 'dark' ? darkStyles : {}} className={className}>
                    {children}
                  </div>
                )}
              />
            </Box>
          )}
        </Box>

        {/* Slider in the middle */}
        <Box sx={{ flexGrow: 1, mx: 4, height: 40, position: 'relative' }}>
          <Typography 
              variant="body2" 
              sx={{ 
                position: 'absolute', 
                left: `${sliderValue}%`, 
                transform: 'translateX(-50%)',
                top: -10,
                whiteSpace: 'nowrap'
              }}
            >
              {formatDate(currentDate)}
          </Typography>
          <Slider
            value={sliderValue}
            onChange={handleSliderChange}
            aria-labelledby="date-range-slider"
            sx={{ mt: 1 }}
          />
          
        </Box>

        {/* End date selector */}
        <Box ref={endDateRef} sx={{ position: 'relative' }}>
          <Button 
            variant="text" 
            size="small"
            onClick={() => setShowEndDatePicker(!showEndDatePicker)}
            sx={{ minWidth: 'auto' }}
          >
            {formatDate(endDate)}
          </Button>
          {showEndDatePicker && (
            <Box sx={{ position: 'absolute', top: '100%', right: 0, zIndex: 10, mt: 1 }}>
              <DatePicker
                selected={endDate}
                onChange={(date) => handleEndDateChange(date!)}
                inline
                minDate={startDate}
                locale={locale}
                calendarClassName={`material-datepicker ${theme.palette.mode}-theme`}
                popperClassName={`material-datepicker-popper ${theme.palette.mode}-theme`}
                popperPlacement="bottom-start"
                wrapperClassName="date-picker-wrapper"
                calendarContainer={({ className, children }) => (
                  <div style={theme.palette.mode === 'dark' ? darkStyles : {}} className={className}>
                    {children}
                  </div>
                )}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}